package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerContactInput
import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.web.theme.AvatarStyle
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.PersonListStyle
import ai.jessie.web.theme.PersonStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.card
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Step 8 and A1 (`16-Firma.png`, Stammdaten and Personen cards). Only what the server returned is
// shown; absent values are never filled in. The detail page carries the name in its title, so
// only the list page's confirmation repeats it here.
@Composable
internal fun customerRecord(current: CustomerResponse, showName: Boolean) {
    Div(attrs = Grid2Style.toAttrs()) {
        card("Stammdaten") {
            TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
                if (showName) definition("Name") { Text(current.displayName) }
                definition("Kundentyp") { Text(customerTypes[current.type] ?: "–") }
                definition("Status") { statusBadge(current.status) }
                definition("E-Mail") { Text(current.email ?: "–") }
                definition("Telefon") { Text(current.phone ?: "–") }
                definition("Adressen") {
                    subList(current.addresses.map { it.summary() }, "Keine Adressen.")
                }
                definition("Kontaktpersonen") {
                    subList(
                        current.contacts.map { it.details.summary() },
                        "Keine Kontaktpersonen.",
                    )
                }
            }
        }
        card("Personen") { people(current.contacts.map { it.details }) }
    }
    // B3: no destination exists yet, so linked records are described, never linked or counted.
    if (current.hasProjectReferences)
        P(attrs = HintStyle.toAttrs()) {
            Text("Projekte verweisen auf diesen Kunden. Die Verknüpfungen bleiben erhalten.")
        }
    P(attrs = HintStyle.toAttrs()) {
        Text("Verknüpfte Projekte und Rechnungen können hier noch nicht geöffnet werden.")
    }
}

@Composable
internal fun definition(term: String, value: @Composable () -> Unit) {
    TagElement<HTMLElement>("dt", null) { Text(term) }
    TagElement<HTMLElement>("dd", null) { value() }
}

@Composable
internal fun subList(items: List<String>, empty: String) {
    if (items.isEmpty()) Text(empty)
    else Ul(attrs = SubListStyle.toAttrs()) { items.forEach { Li { Text(it) } } }
}

@Composable
internal fun statusBadge(status: String) {
    badge(statusLabel(status), if (status == "ACTIVE") BadgeTone.HOURS else BadgeTone.NEUTRAL)
}

// Contacts with initials; role and e-mail form the muted detail line.
@Composable
private fun people(contacts: List<CustomerContactInput>) {
    if (contacts.isEmpty()) {
        P(attrs = MutedStyle.toAttrs()) { Text("Keine Kontaktpersonen.") }
        return
    }
    Ul(attrs = PersonListStyle.toAttrs()) {
        contacts.forEach { contact ->
            Li(attrs = PersonStyle.toAttrs()) {
                Span(attrs = AvatarStyle.toAttrs { attr("aria-hidden", "true") }) {
                    Text(initials(contact.name))
                }
                Div(attrs = StackStyle.toAttrs()) {
                    Span(attrs = TableStrongStyle.toAttrs()) { Text(contact.name) }
                    val detail =
                        listOfNotNull(
                            contact.role,
                            contact.email,
                            "Rechnungskontakt".takeIf { contact.billingContact },
                        )
                    if (detail.isNotEmpty())
                        Span(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                            Text(detail.joinToString(" · "))
                        }
                }
            }
        }
    }
}

internal fun initials(name: String): String =
    name
        .split(' ')
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") {
            it.first().uppercaseChar().toString()
        }
